import GradientLoadingAnimation from 'components/Reusables/GradientLoadingAnimation'
import { Copy, ExternalLink } from 'lucide-react'
import { FiMinimize2 } from 'react-icons/fi'
import { toast } from 'react-toastify'
import LiveListen from './LiveListen'
import { Button } from '@heroui/react'
import { FormProvider, useForm } from 'react-hook-form'
import PhoneInput from 'components/Hero/PhoneInput'
import { useState } from 'react'
import { $fetch } from 'utils/fetch'


export function TranscriptPanel({
  onClose,
  transcripts = [],
  selectedCallId,
  isLoading,
  error,
  onNavigateToCallLogs,
  isCallEnded,
}) {
  const getRoleStyles = (role) => {
    switch (role) {
      case 'assistant':
        return 'bg-blue-50/80 border-l-3 border-blue-400 hover:bg-blue-50'
      case 'user':
        return 'bg-indigo-50/80 border-l-3 border-indigo-400 hover:bg-indigo-50'
      case 'agent-action':
        return 'bg-violet-50/80 border-l-3 border-violet-400 hover:bg-violet-50'
      default:
        return 'bg-white'
    }
  }

  const [isTransferCallModalOpen, setIsTransferCallModalOpen] = useState(false);
  const [isSending, setIsSending] = useState(false)

  const handleTransferCall = async () => {
    setIsSending(true)
    try {
      const values = methods.getValues()
      await $fetch('v1/calls/active/transfer', {
        method: 'POST',
        body: JSON.stringify({
          call_id: selectedCallId,
          transfer_number: values.phone_number,
        }),
      })
      setIsTransferCallModalOpen(false)
      toast.success('Call transferred successfully')
    } catch (error) {
      toast.error('Failed to transfer call')
    } finally {
      setIsSending(false)
    }
  }


  const methods = useForm({
    defaultValues: {
      phone_number: '',
    },
  })

  return (
    <div className="h-full flex flex-col bg-white relative">
      <div className="px-5 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-base font-semibold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
              Live Transcript
            </h3>
            <div className="flex items-center space-x-2 mt-1.5">
              <p className="text-xs text-slate-600">
                Call ID:
                {' '}
                {selectedCallId}
              </p>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(selectedCallId)
                  toast.success('Call ID copied to clipboard')
                }}
                className="text-slate-400 hover:text-blue-500 transition-all duration-200 hover:scale-110"
              >
                <Copy size={14} />
              </button>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700"
          >
            <FiMinimize2 size={20} />
          </button>
        </div>
      </div>
      <div className="p-5 space-y-4 overflow-y-auto flex-1">
        {isLoading
          ? (
              <div className="flex justify-center items-center h-full">
                <GradientLoadingAnimation
                  duration={10}
                  variant="mintForest"
                  message="Waiting for transcripts..."
                />
              </div>
            )
          : error
            ? (
                <div className="p-2.5 bg-red-50 text-red-700 rounded-lg">{error}</div>
              )
            : selectedCallId && transcripts?.length > 0
              ? (
                  <>
                    <div className="flex space-x-2 h-12.5">
                      <div className="flex-grow w-[90%] p-2.5 bg-gradient-to-r from-slate-100 to-slate-50 rounded-md border border-slate-200 shadow-sm">
                        <div className="flex-grow">
                          {isCallEnded
                            ? (
                                <>
                                  <p className="text-slate-700 font-medium mb-2">Call ended</p>
                                  <button
                                    onClick={onNavigateToCallLogs}
                                    className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-700 transition-colors"
                                  >
                                    <span>View full call details</span>
                                    <ExternalLink size={14} />
                                  </button>
                                </>
                              )
                            : (
                                <>
                                  <p className="text-slate-700 font-medium mb-2">Call Active</p>
                                  <p>
                                    Started At:
                                    {' '}
                                    {new Date(transcripts[0].timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })}
                                  </p>
                                </>
                              )}
                        </div>
                      </div>
                      <div className="w-16 flex items-center justify-center p-2.5 bg-gradient-to-r from-slate-100 to-slate-50 rounded-md border border-slate-200 shadow-sm">
                        <LiveListen callId={selectedCallId} />
                      </div>
                    </div>
                    <div className="mt-4">
           <div className="flex items-center justify-center gap-2">
            <Button
              size="sm"
              className="w-full flex items-center justify-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 shadow-sm"
              onPress={(e) => {
                setIsTransferCallModalOpen(prev => !prev)
              }}
            >
              <span>Transfer Live Call</span>
              <svg
                className={`w-4 h-4 transition-transform ${ isTransferCallModalOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>

            </Button>
            </div>
            {isTransferCallModalOpen && (
              <div className="mt-2 space-y-2">
                <FormProvider {...methods}>
                  <div className="space-y-4 items-center mt-2">
                    <div>
                      <PhoneInput
                        value={methods.watch('phone_number')}
                        onChange={value => methods.setValue('phone_number', value)}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between w-full">
                    <Button
                      size="sm"
                      className="w-full bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors duration-200"
                      onPress={handleTransferCall}
                      disabled={isSending}
                    >
                      {isSending ? 'Transferring...' : 'Transfer'}
                    </Button>
                  </div>
                </FormProvider>
              </div>
            )}

          </div>
                   

                    {transcripts.map(transcript => (
                      <div
                        key={transcript.transcript_id}
                        className={`p-3 rounded-md shadow-sm space-y-2 transition-all duration-200 ${getRoleStyles(
                          transcript.role,
                        )}`}
                      >
                        <div className="flex justify-between items-center">
                          <span className="text-xs font-semibold text-slate-700">
                            {transcript.role.charAt(0).toUpperCase()
                              + transcript.role.slice(1)}
                          </span>
                          {transcript.timestamp && (
                            <span className="text-2xs text-slate-500bg-white/50 px-1.5 py-0.5 rounded-full">
                              {new Date(transcript.timestamp).toLocaleTimeString()}
                            </span>
                          )}
                        </div>
                        <p className="text-slate-700 text-smleading-relaxed whitespace-pre-wrap">
                          {transcript.text}
                        </p>
                      </div>
                    ))}
                  </>
                )
              : (
                  <p className="text-slate-500 text-center">
                    No transcripts available yet
                  </p>
                )}
      </div>
    </div>
  )
}
